/**
 * Link to Library Dialog - Link message to existing library
 * Allows creating library alias and/or section
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  Box,
  Typography,
  Alert,
  Divider,
  Autocomplete,
} from '@mui/material';
import { useLibraries } from '@/hooks/useLibraries';
import { useCreateLibraryAlias } from '@/hooks/useLibraryAliases';
import { useCreateLibrarySection } from '@/hooks/useLibrarySections';
import { LibraryAliasType, LibrarySectionSource } from '@/types/enums';

interface LinkToLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  chatId: number | null;
  messageId: number | null;
  messageText?: string;
  fileName?: string;
  onSuccess?: () => void;
}

/**
 * Extract potential alias from filename
 * Removes extension and trailing numbers
 * e.g., "FAZILAHMEDPS2.pdf" -> "FAZILAHMEDPS"
 */
function extractAliasFromFilename(fileName: string): string {
  // Remove extension
  const withoutExt = fileName.replace(/\.[^/.]+$/, '');
  // Remove trailing numbers
  const withoutNumbers = withoutExt.replace(/\d+$/, '');
  return withoutNumbers;
}

/**
 * Extract hashtags from message text
 */
function extractHashtags(text: string): string[] {
  const matches = text.match(/#[\u0600-\u06FF\w_]+/g);
  return matches || [];
}

const LinkToLibraryDialog: React.FC<LinkToLibraryDialogProps> = ({
  open,
  onClose,
  chatId,
  messageId,
  messageText = '',
  fileName = '',
  onSuccess,
}) => {
  const { t } = useTranslation('components');
  // State
  const [selectedLibraryId, setSelectedLibraryId] = useState<number | null>(null);
  const [aliasText, setAliasText] = useState('');
  const [aliasType, setAliasType] = useState<LibraryAliasType>(LibraryAliasType.TEXT);
  const [createSection, setCreateSection] = useState(false);
  const [startMessageId, setStartMessageId] = useState<string>('');
  const [endMessageId, setEndMessageId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Hooks
  const { data: libraries = [], isLoading: librariesLoading } = useLibraries();
  const createAliasMutation = useCreateLibraryAlias();
  const createSectionMutation = useCreateLibrarySection();

  // Extract suggestions from message/filename
  const aliasSuggestions = useMemo(() => {
    const suggestions: string[] = [];

    // From filename
    if (fileName) {
      const extracted = extractAliasFromFilename(fileName);
      if (extracted && extracted.length >= 2) {
        suggestions.push(extracted);
      }
    }

    // From hashtags
    const hashtags = extractHashtags(messageText);
    hashtags.forEach(tag => {
      // Remove # prefix
      const tagText = tag.substring(1);
      if (tagText.length >= 2) {
        suggestions.push(tagText);
      }
    });

    return [...new Set(suggestions)]; // Remove duplicates
  }, [fileName, messageText]);

  // Initialize fields when dialog opens
  useEffect(() => {
    if (open && messageId !== null) {
      setStartMessageId(String(messageId));
      setEndMessageId('');
      setError(null);

      // Pre-fill alias text with first suggestion
      if (aliasSuggestions.length > 0) {
        setAliasText(aliasSuggestions[0]);
        // Auto-detect if it's a hashtag
        const hashtags = extractHashtags(messageText);
        if (hashtags.some(h => h.substring(1) === aliasSuggestions[0])) {
          setAliasType(LibraryAliasType.HASHTAG);
        } else {
          setAliasType(LibraryAliasType.TEXT);
        }
      } else {
        setAliasText('');
        setAliasType(LibraryAliasType.TEXT);
      }
    }
  }, [open, messageId, aliasSuggestions, messageText]);

  // Reset when dialog closes
  const handleClose = () => {
    setSelectedLibraryId(null);
    setAliasText('');
    setAliasType(LibraryAliasType.TEXT);
    setCreateSection(false);
    setStartMessageId('');
    setEndMessageId('');
    setNotes('');
    setError(null);
    onClose();
  };

  // Validate form
  const isValid = useMemo(() => {
    if (!selectedLibraryId) return false;

    // Must have at least alias OR section
    const hasAlias = aliasText.trim().length >= 2;
    const hasSection = createSection;

    if (!hasAlias && !hasSection) return false;

    // If section, must have valid start ID
    if (hasSection) {
      const start = parseInt(startMessageId, 10);
      if (isNaN(start) || start < 1) return false;

      // If end provided, must be >= start
      if (endMessageId.trim()) {
        const end = parseInt(endMessageId, 10);
        if (isNaN(end) || end < start) return false;
      }
    }

    return true;
  }, [selectedLibraryId, aliasText, createSection, startMessageId, endMessageId]);

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedLibraryId || chatId === null) return;

    setError(null);

    try {
      // Create alias if provided
      if (aliasText.trim().length >= 2) {
        await createAliasMutation.mutateAsync({
          libraryId: selectedLibraryId,
          data: {
            alias: aliasText.trim(),
            alias_type: aliasType,
          },
        });
      }

      // Create section if checked
      if (createSection) {
        const start = parseInt(startMessageId, 10);
        const end = endMessageId.trim() ? parseInt(endMessageId, 10) : start;

        await createSectionMutation.mutateAsync({
          libraryId: selectedLibraryId,
          data: {
            chat_id: chatId,
            start_message_id: start,
            end_message_id: end,
            source: LibrarySectionSource.MANUAL,
            notes: notes.trim() || undefined,
          },
        });
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('linkToLibraryDialog.errors.failedToLink'));
    }
  };

  const isSubmitting = createAliasMutation.isPending || createSectionMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('linkToLibraryDialog.title')}</DialogTitle>

      <DialogContent>
        {/* Message Reference Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          {messageText && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('linkToLibraryDialog.messageInfo.message')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  direction: 'rtl',
                  textAlign: 'right',
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                }}
              >
                {messageText}
              </Typography>
            </Box>
          )}
          {fileName && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('linkToLibraryDialog.messageInfo.file')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {fileName}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('linkToLibraryDialog.messageInfo.messageId', { messageId })}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Library Selection */}
        <Autocomplete
          options={libraries}
          getOptionLabel={(lib) => lib.canonical_name || ''}
          value={libraries.find(l => l.id === selectedLibraryId) || null}
          onChange={(_, newValue) => setSelectedLibraryId(newValue?.id || null)}
          loading={librariesLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('linkToLibraryDialog.librarySelection.label')}
              required
              fullWidth
              sx={{ mb: 3 }}
            />
          )}
        />

        <Divider sx={{ my: 2 }} />

        {/* Alias Section */}
        <Typography variant="subtitle2" gutterBottom>
          {t('linkToLibraryDialog.aliasSection.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t('linkToLibraryDialog.aliasSection.subtitle')}
        </Typography>

        <TextField
          label={t('linkToLibraryDialog.aliasSection.aliasLabel')}
          value={aliasText}
          onChange={(e) => setAliasText(e.target.value)}
          fullWidth
          placeholder={t('linkToLibraryDialog.aliasSection.aliasPlaceholder')}
          helperText={
            aliasSuggestions.length > 0
              ? t('linkToLibraryDialog.aliasSection.aliasHelper', { suggestions: aliasSuggestions.join(', ') })
              : t('linkToLibraryDialog.aliasSection.aliasHelperNoSuggestions')
          }
          sx={{ mb: 2 }}
        />

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">{t('linkToLibraryDialog.aliasSection.aliasType')}</FormLabel>
          <RadioGroup
            row
            value={aliasType}
            onChange={(e) => setAliasType(e.target.value as LibraryAliasType)}
          >
            <FormControlLabel
              value={LibraryAliasType.TEXT}
              control={<Radio size="small" />}
              label={t('linkToLibraryDialog.aliasSection.aliasTypeText')}
            />
            <FormControlLabel
              value={LibraryAliasType.HASHTAG}
              control={<Radio size="small" />}
              label={t('linkToLibraryDialog.aliasSection.aliasTypeHashtag')}
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Section Creation */}
        <FormControlLabel
          control={
            <Checkbox
              checked={createSection}
              onChange={(e) => setCreateSection(e.target.checked)}
            />
          }
          label={t('linkToLibraryDialog.sectionCreation.checkboxLabel')}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t('linkToLibraryDialog.sectionCreation.checkboxSubtitle')}
        </Typography>

        {createSection && (
          <Box sx={{ pl: 4, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label={t('linkToLibraryDialog.sectionCreation.startMessageIdLabel')}
                type="number"
                value={startMessageId}
                onChange={(e) => setStartMessageId(e.target.value)}
                size="small"
                required
                sx={{ width: 150 }}
              />
              <TextField
                label={t('linkToLibraryDialog.sectionCreation.endMessageIdLabel')}
                type="number"
                value={endMessageId}
                onChange={(e) => setEndMessageId(e.target.value)}
                size="small"
                placeholder={t('linkToLibraryDialog.sectionCreation.endMessageIdPlaceholder')}
                helperText={t('linkToLibraryDialog.sectionCreation.endMessageIdHelper')}
                sx={{ width: 200 }}
              />
            </Box>
            <TextField
              label={t('linkToLibraryDialog.sectionCreation.notesLabel')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder={t('linkToLibraryDialog.sectionCreation.notesPlaceholder')}
            />
          </Box>
        )}

        {/* Validation hint */}
        {selectedLibraryId && !aliasText.trim() && !createSection && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('linkToLibraryDialog.validationHint')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t('linkToLibraryDialog.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? t('linkToLibraryDialog.actions.linking') : t('linkToLibraryDialog.actions.link')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkToLibraryDialog;
